import * as React from 'react';
import { Component } from 'react';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { Child } from 'Src/models/child';
import './Children.scss';
import { getChildren, setChildAvatar } from '../../../../helpers/service/parent/children';
import { uploadImage } from '../../../../helpers/service/image';
import { History } from 'history';
import propz from 'propz';
import { ParentPageHeader } from '../components/ParentPageHeader';
import { getChildrenCardStats } from 'Src/helpers/service/parent/messagesTable';
import { getSearchFilter } from 'Src/helpers/table/table';

interface Props {
  user: AppUser;
  history: History;
}

interface State {
  isLoading: boolean;
  children: Child[];
  error: string;
  stats: Record<string, { consentRequests: number; awards: number }>;
}

export class Children extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      children: [],
      error: '',
      stats: {}
    };
  }

  componentDidMount() {
    const { user } = this.props;

    this.setState({
      isLoading: true
    });

    getChildren(user)
      .then(children => {
        this.setState({ children, isLoading: false });
      })
      .catch(() => this.setState({ isLoading: false, error: 'We could not load the linked children. Please try again.' }));

    getChildrenCardStats(user)
      .then(stats => this.setState({ stats }))
      .catch(() => undefined);
  }

  onAvatarChange = (event, childId, index) => {
    const avatarFile = event.target.files[0];
    const { user } = this.props;

    if (!avatarFile) return;

    this.setState({
      isLoading: true
    });

    uploadImage(user, avatarFile)
      .then(res => {
        const { key } = res;
        const avatarUrl = `${window.apiImg}/images/${key}`;
        return setChildAvatar(user, childId, avatarUrl);
      })
      .then((child: Child) => {
        const { children } = this.state;
        const childrenUpdated = [...children];
        childrenUpdated[index] = child;

        this.setState({
          children: childrenUpdated,
          isLoading: false
        });
      })
      .catch(() => this.setState({ isLoading: false, error: 'The profile photo could not be updated.' }));
  };

  render() {
    const { isLoading, children, error, stats } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <main className="parentPage parentChildrenPage">
        <ParentPageHeader
          title="Children"
          chips={[
            { value: children.length, label: children.length === 1 ? 'linked child' : 'linked children', tone: 'blue' },
            {
              value: new Set(children.map(child => child.schoolId).filter(Boolean)).size,
              label: 'schools',
              tone: 'slate'
            }
          ]}
        />
        {error ? <div className="parentChildrenPage__error">{error}</div> : null}
        {children.length === 0 ? (
          <section className="parentCard parentChildrenPage__empty">
            <div aria-hidden="true">👤</div>
            <h2>No children are linked yet</h2>
            <p>When your parent role is linked to a student, their profile will appear here.</p>
          </section>
        ) : (
          <div className="parentChildrenGrid">
        {children.map((child, index) => {
          const { avatar, id } = child;
          const childStats = stats[id] || { consentRequests: 0, awards: 0 };
          const childFilter = getSearchFilter({ childIdList: [id] });
          const isAvatarUrlExist = typeof avatar !== 'undefined' && avatar !== '';
          const formName = child.formName || propz.get(child, ['form', 'name'], '');
          const houseName = propz.get(child, ['house', 'name'], '');
          const initials = `${child.firstName || ''} ${child.lastName || ''}`
            .trim()
            .split(/\s+/)
            .slice(0, 2)
            .map(part => part.charAt(0).toUpperCase())
            .join('');
          return (
            <article className="parentChildCard parentCard" key={id || `children_${index}`}>
              <div className="parentChildCard__profile">
                <div className="parentChildCard__avatarWrap">
                {isAvatarUrlExist ? (
                    <div style={{ backgroundImage: `url(${avatar})` }} className="parentChildCard__avatar" />
                ) : (
                    <div className="parentChildCard__avatar parentChildCard__avatar--letters">
                      {initials}
                  </div>
                )}
              <input
                type="file"
                    className="parentChildCard__fileInput"
                id={`children_tab_file_input_${index}`}
                accept=".jpg, .jpeg, .png"
                    onChange={event => this.onAvatarChange(event, id, index)}
              />
                  <label className="parentChildCard__photoButton" htmlFor={`children_tab_file_input_${index}`} title="Change profile photo">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8h4l1.5-2h5L16 8h4v11H4z" /><circle cx="12" cy="13" r="3.5" /></svg>
                </label>
              </div>
                <div className="parentChildCard__identity">
                  <h2>{child.firstName} {child.lastName}</h2>
                </div>
              </div>

              <div className="parentChildCard__chips">
                {child.schoolName ? (
                  <span className="parentChildCard__chip--school">
                    <i className="parentChildCard__schoolLogo" aria-hidden="true">
                      {child.schoolPic ? <img src={child.schoolPic} alt="" /> : child.schoolName.charAt(0).toUpperCase()}
                    </i>
                    {child.schoolName}
                  </span>
                ) : null}
                {formName ? <span><b>Form</b>{formName}</span> : null}
                {houseName ? <span className="parentChildCard__chip--amber"><b>House</b>{houseName}</span> : null}
              </div>

              <div className="parentChildCard__actions">
                <button type="button" onClick={() => this.props.history.push(`/calendar?childId=${id}`)}>Calendar</button>
                <button type="button" onClick={() => this.props.history.push({ pathname: '/awards/current', search: childFilter })}>
                  Awards <span className="parentChildCard__countChip parentChildCard__countChip--awards">{childStats.awards}</span>
                </button>
                <button type="button" onClick={() => this.props.history.push({ pathname: '/consentRequests/inbox', search: childFilter })}>
                  Consents <span className={`parentChildCard__countChip ${childStats.consentRequests > 0 ? 'parentChildCard__countChip--attention' : 'parentChildCard__countChip--empty'}`}>{childStats.consentRequests}</span>
                </button>
              </div>
            </article>
          );
        })}
          </div>
        )}
      </main>
    );
  }
}
